import { Tabs as T } from "radix-ui";
import type { ComponentProps } from "react";

import { cn } from "@/lib/cn";

export const Tabs = T.Root;

export function TabsList({ className, ...props }: ComponentProps<typeof T.List>) {
  return (
    <T.List
      className={cn(
        "inline-flex h-9 max-w-full items-center gap-0.5 overflow-x-auto rounded-lg bg-surface-2 p-0.5",
        className,
      )}
      {...props}
    />
  );
}

export function TabsTrigger({ className, ...props }: ComponentProps<typeof T.Trigger>) {
  return (
    <T.Trigger
      className={cn(
        "inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-xs font-medium whitespace-nowrap text-muted",
        "transition-colors hover:text-fg",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30",
        "data-[state=active]:bg-surface data-[state=active]:text-fg data-[state=active]:shadow-card",
        className,
      )}
      {...props}
    />
  );
}

export function TabsContent({ className, ...props }: ComponentProps<typeof T.Content>) {
  return <T.Content className={cn("mt-3 focus-visible:outline-none", className)} {...props} />;
}

/** A small segmented control built on tabs, for switching a view in place. */
export function Segmented<V extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: V;
  onChange: (v: V) => void;
  options: readonly { value: V; label: string }[];
  label: string;
}) {
  return (
    <Tabs value={value} onValueChange={(v) => onChange(v as V)}>
      <TabsList aria-label={label} className="h-8">
        {options.map((o) => (
          <TabsTrigger key={o.value} value={o.value} className="h-7 px-2.5">
            {o.label}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
